import { animate, style, transition, trigger } from '@angular/animations'
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router'

import { AngularReactModule } from '@bubblydoo/angular-react'
import { Store } from '@ngrx/store'
import { OAuthService } from 'angular-oauth2-oidc'
import { last as _last } from 'lodash'
import { isEqual as _isEqual } from 'lodash/fp'
import { TooltipModule } from 'primeng/tooltip'

import { EMPTY, Observable, Subscription, switchMap, tap } from 'rxjs'
import { distinctUntilChanged, filter, map } from 'rxjs/operators'

import { FlxButtonComponent, FlxCardModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { StartMode, getProcessInstanceUuidFromUrl, resetStores, urlUpdate$ } from '@flowx/core-sdk'
import { CommentModeStore } from '@flowx/designer/modules/ai-builder/store/comment-mode.store'
import { getTenantId } from '@flowx/designer/modules/auth/tenant-id.state'
import {
  ProcessDefinition,
  ProcessState,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { FlxClickStopPropagationDirective } from '@flowx/designer/modules/core/directives/stop-propagation.directive'
import { RunDesignerLinkStore } from '@flowx/designer/modules/layout/store/run-designer-link.store'
import { ProcessInstanceInfoComponent } from '@flowx/designer/modules/process/components/process-instance-info/process-instance-info.component'
import { ProcessInstanceReactFlowComponent } from '@flowx/designer/modules/process/components/process-instance-react-flow/process-instance-react-flow.component'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import {
  ApplicationStatus,
  ApplicationType,
} from '@flowx/designer/shared/entities/application.entity'
import {
  ApplicationProcessData,
  ProcessInstanceBreadcrumb,
} from '@flowx/designer/shared/entities/process-instance.types'
import { FlxNavigationService } from '@flowx/designer/shared/services/navigation.service'
import { ProcessDefinitionService } from '@flowx/designer/shared/services/process-definition.service'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import {
  ProcessApiParameters,
  UrlParametersStoreService,
} from '@flowx/designer/shared/services/url-parameters-store.service'
import { selectRouteParam } from '@flowx/designer/shared/store/selectors/router.selectors'
import { FlxProcessRendererPortal } from '@flowx/react-sdk'

@Component({
  selector: 'app-process-running-instance',
  templateUrl: './process-running-instance.page.html',
  styleUrls: ['./process-running-instance.page.scss', '../../../../styles/versioning.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeIn', [
      transition('void => *', [
        style({ transform: 'translateY(100vh)' }),
        animate(300, style({ transform: 'translateY(0)' })),
      ]),
      transition('* => void', [
        style({ transform: 'translateY(0)' }),
        animate(300, style({ transform: 'translateY(100vh)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlxButtonComponent,
    TooltipModule,
    RouterLink,
    FlxClickStopPropagationDirective,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ProcessInstanceReactFlowComponent,
    FlxIconModule,
    ProcessInstanceInfoComponent,
    FlxCardModule,
    AngularReactModule,
  ],
})
export class ProcessInstancePageComponent implements OnInit, OnDestroy {
  #destroyRef = inject(DestroyRef)
  #urlParamsStore = inject(UrlParametersStoreService)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #processDefinitionService = inject(ProcessDefinitionService)
  #navigationService = inject(FlxNavigationService)
  #route = inject(ActivatedRoute)
  #processInstanceService = inject(ProcessInstanceService)
  #router = inject(Router)
  #authService = inject(OAuthService)
  #runDesignerLink = inject(RunDesignerLinkStore)
  #store = inject(Store)
  #commentMode = inject(CommentModeStore)
  #appVersionId = this.#store.selectSignal(selectRouteParam('appVersionId'))
  // The run the renderer shows, latched from the SDK's first URL update, or from the URL on a
  // reload, and never replaced: a subprocess moves the SDK's `?processInstanceUuid` (`processUuid`
  // before) to the child while the renderer keeps showing this run. The breadcrumbs are no source,
  // since an embedded process leaves its parent's uuid out of them.
  #rootRunUuid = signal<string | null>(null)
  #withdrawCommentMode: (() => void) | null = null

  private subscriptions = new Subscription()

  baseApiUrl = inject<string>('BASE_URL' as any)
  staticAssetsPath = inject<string>('STATIC_ASSETS_PATH' as any)
  processApiPath = inject<string>('PROCESS_API_PATH' as any)
  legacyHttpVersion = inject<boolean>('LEGACY_HTTP_VERSION' as any)

  readonly ProcessState = ProcessState
  readonly FlxProcessRendererPortal = FlxProcessRendererPortal

  applicationProcessData = signal<ApplicationProcessData>(null)
  loadingApplicationProcessData = signal<boolean>(false)
  processName: string
  processStartData: Record<string, any>
  processUuid = signal<string | null>(null)
  runFromBuild = false
  debugCustomComponents: boolean

  definitionsExist = signal(false)
  appTypeIcon = computed(() => {
    const applicationProcessData = this.applicationProcessData()
    if (!applicationProcessData?.applicationDetails) {
      return ''
    }
    return applicationProcessData.applicationDetails.applicationType === ApplicationType.APP
      ? 'ph-folder'
      : 'ph-books'
  })
  processDefinition$: Observable<ProcessDefinition> =
    this.#processDefinitionService.processDefinition$
  processDefinition = null
  showProcessInfoDetails = false
  instanceDataOpened = signal(false)
  themeId = signal('')
  fromBuild = false
  authToken = signal(localStorage.getItem('access_token'))
  locale = signal<string>(null)
  appInfo: {
    // TODO: update this when we refactor application -> project in apis
    projectId: string
    appVersionId: string
    selectedProcessResourceId: string
  }
  buildId: string
  workspaceId: string
  language = signal<string>(null)
  startMode: StartMode = StartMode.MANUAL
  applicationDetailsLoaded = this.#applicationDetailsStore.applicationDetailsLoaded

  organizationId = getTenantId()

  reactProps = computed(() => ({
    baseUrl: this.baseApiUrl,
    assetsBaseUrl: this.staticAssetsPath,
    enginePath: this.processApiPath,
    processName: this.processName,
    processStartData: this.processStartData,
    legacyHttpVersion: this.legacyHttpVersion,
    debugLogs: true,
    isDraft: true,
    themeId: this.themeId(),
    accessToken: this.authToken(),
    projectInfo: this.appInfo,
    locale: this.locale(),
    language: this.language(),
    buildId: this.buildId,
    workspaceId: this.workspaceId,
    organizationId: this.organizationId,
    cache: false,
    startMode: this.startMode,
    commentMode: this.#commentMode.on(),
  }))

  constructor() {
    // Comment mode is for a run that started from the head of its branch: the work in progress, or
    // the latest commit. The store keeps the previous version until this run's one arrives, so a
    // version that is not the run's counts as not at the head.
    effect(() => {
      const version = this.#applicationDetailsStore.applicationVersion()
      const usedByRun = !!version && version.id === this.#appVersionId()

      this.#commentMode.reportRunVersion({
        atBranchHead:
          usedByRun && (version.status === ApplicationStatus.WIP || version.lastVersionInBranch),
        branchName: usedByRun ? version.branchName : '',
      })
    })
  }

  ngOnInit(): void {
    this.setUpTokenListener()

    resetStores()
    const params = this.#urlParamsStore.urlParameters

    this.appInfo = {
      projectId: params.appId,
      appVersionId: params.appVersionId,
      selectedProcessResourceId: params.selectedProcessResourceId,
    }

    this.workspaceId = params.workspaceId
    this.#applicationDetailsStore.getApplicationVersion(params.appId, params.appVersionId)
    this.#withdrawCommentMode = this.#commentMode.offerFromPage(() => ({
      resourceType: 'process',
      resourceId: this.appInfo.selectedProcessResourceId,
      runId: this.#rootRunUuid() ?? '',
      appVersionId: this.appInfo.appVersionId,
      resourceName: this.processDefinition?.resourceName || this.processName || 'Process',
      resourceDefinitionId: this.processDefinition?.resourceDefinitionId,
    }))
    // Where Config leads after this run: the process definition, as the page's own button goes to it.
    this.#runDesignerLink.remember({
      appId: params.appId,
      commands: [
        '/',
        'guard',
        'workspace',
        params.workspaceId,
        'projects',
        params.appId,
        'config',
        params.appVersionId,
        'processes',
        params.selectedProcessResourceId,
      ],
    })
    // TODO: update this when we refactor application -> project in apis
    this.getProcessDefinitionData({
      appId: this.appInfo.projectId,
      appVersionId: this.appInfo.appVersionId,
      workspaceId: this.workspaceId,
      selectedProcessResourceId: this.appInfo.selectedProcessResourceId,
    })

    const state = window.history.state
    if (state) {
      this.processName = state.processName
      this.processStartData = state.processStartData
      this.debugCustomComponents = state.debugCustomComponents
      this.fromBuild = state.fromBuild
      this.buildId = state.buildId
      this.startMode = state.startMode || StartMode.MANUAL
    }

    urlUpdate$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((url) => {
      // A fresh run's id reaches the URL through the SDK, not the router, so no route param has it.
      const processInstanceUuid = getProcessInstanceUuidFromUrl(url)
      this.#rootRunUuid.update((root) => root ?? processInstanceUuid)
      this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { processInstanceUuid },
        queryParamsHandling: 'merge',
        skipLocationChange: true,
      })
    })

    this.#route.parent.queryParams
      .pipe(
        // processUuid is the name links made before the SDK rename carry.
        map(({ processInstanceUuid, processUuid, ...params }: Params): Params => ({
          ...params,
          processInstanceUuid: processInstanceUuid ?? processUuid,
        })),
        tap(
          ({
            instanceDataOpened,
            processInstanceUuid,
            locale,
            themeId,
            language,
            runFromBuild,
          }) => {
            if (processInstanceUuid) {
              this.processUuid.set(processInstanceUuid)
            }
            this.#rootRunUuid.update((root) => root ?? processInstanceUuid ?? null)
            this.locale.set(locale)
            this.themeId.set(themeId)
            this.language.set(language)
            this.runFromBuild = runFromBuild === 'true'
            this.instanceDataOpened.set(instanceDataOpened === 'true')
          }
        ),
        filter(({ processInstanceUuid }) => !!processInstanceUuid),
        /* Get process instance data on the first emission regardless of instanceDataOpened and only if instanceDataOpened is true for subsequent emissions */
        map(
          (
            { processInstanceUuid, instanceDataOpened, embedId, context, showEditVariables },
            index
          ) => ({
            processInstanceUuid,
            instanceDataOpened: index === 0 || instanceDataOpened,
            embedId: embedId || null,
            context: context || null,
            showEditVariables,
          })
        ),
        distinctUntilChanged(_isEqual),
        switchMap(({ processInstanceUuid, instanceDataOpened, embedId, context }) =>
          instanceDataOpened
            ? this.getProcessInstanceData(processInstanceUuid, embedId, context)
            : EMPTY
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()

    this.processDefinition$
      .pipe(
        tap((processDefinition) => {
          this.processDefinition = processDefinition
          this.definitionsExist.set(true)
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  showInstanceData(): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        instanceDataOpened: true,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  toggleProcessDefinitionInfoCard(): void {
    this.showProcessInfoDetails = !this.showProcessInfoDetails
  }

  hideInstanceData(): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        instanceDataOpened: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  getProcessDefinitionData(processParams: ProcessApiParameters): void {
    this.#processDefinitionService
      .getApplicationProcess(processParams)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe()
  }

  navigateToSubprocess({
    subprocessUuid,
    name,
    uuid,
    previousEmbedId,
    previousEmbedContext,
    breadcrumbs,
  }: {
    subprocessUuid: string
    name: string
    uuid: string
    previousEmbedId?: string
    previousEmbedContext?: string
    breadcrumbs: ProcessInstanceBreadcrumb[]
  }): void {
    this.#router.navigate([], {
      queryParams: {
        processInstanceUuid: subprocessUuid,
        embedId: null,
        context: null,
        breadcrumbs: JSON.stringify([
          ...breadcrumbs,
          {
            processUuid: this.processUuid(),
            name,
            uuid,
            previousEmbedId,
            previousEmbedContext,
          },
        ]),
      },
      relativeTo: this.#route,
      queryParamsHandling: 'merge',
    })
  }

  onNavigateToProcessDefinition(): void {
    this.#navigationService.navigateToProcessDefinition(this.appInfo.selectedProcessResourceId)
  }

  onNavigateToHome(): void {
    if (this.runFromBuild) {
      this.#navigationService.navigateToApplicationRuntime()
    } else {
      this.#navigationService.navigateToApplicationVersion()
    }
  }

  navigateToEmbeddedProcess({
    embedId,
    context,
    name,
    uuid,
    previousEmbedId,
    previousEmbedContext,
    breadcrumbs,
  }: {
    embedId: string
    context: string
    name: string
    uuid: string
    previousEmbedId?: string
    previousEmbedContext?: string
    breadcrumbs: ProcessInstanceBreadcrumb[]
  }): void {
    this.#router.navigate([], {
      queryParams: {
        processInstanceUuid: uuid,
        embedId,
        context,
        breadcrumbs: JSON.stringify([
          ...breadcrumbs,
          {
            name,
            uuid,
            previousEmbedContext,
            previousEmbedId,
          },
        ]),
      },
      relativeTo: this.#route,
      queryParamsHandling: 'merge',
    })
  }

  backToParentProcess({ breadcrumbs }: { breadcrumbs: ProcessInstanceBreadcrumb[] }): void {
    const lastBreadcrumb = _last(breadcrumbs)
    const processUuid = lastBreadcrumb ? lastBreadcrumb?.uuid : null
    const embedId = lastBreadcrumb ? lastBreadcrumb?.previousEmbedId : null
    const context = lastBreadcrumb ? lastBreadcrumb.previousEmbedContext : null

    this.#router.navigate([], {
      queryParams: {
        processInstanceUuid: processUuid,
        embedId,
        context,
        breadcrumbs: JSON.stringify(breadcrumbs.slice(0, -1)),
      },
      relativeTo: this.#route,
      queryParamsHandling: 'merge',
    })
  }

  setUpTokenListener(): void {
    this.#authService.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event) => {
      if (event.type === 'token_received') {
        this.authToken.set(this.#authService.getAccessToken())
      }
    })
  }

  getProcessInstanceData(
    processUuid: string,
    embedId?: string,
    context?: string
  ): Observable<ApplicationProcessData> {
    if (!processUuid) {
      return EMPTY
    }
    this.loadingApplicationProcessData.set(true)
    const nodeId = !isNaN(parseInt(embedId, 10)) ? parseInt(embedId, 10) : null
    return this.#processInstanceService.getProcessInstance(processUuid, nodeId, context).pipe(
      tap((applicationProcessData) => {
        this.loadingApplicationProcessData.set(false)
        if (applicationProcessData) {
          this.applicationProcessData.set(applicationProcessData)
        }
      }),
      takeUntilDestroyed(this.#destroyRef)
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
    this.#withdrawCommentMode?.()
  }
}
